//@ts-check
'use strict';

/**
 * Odds Guide — Sport Nav (CDN-1869).
 *
 * Frontend behaviour for `acf/odds-guide-sport-nav`:
 *   - smooth-scroll to the target section on click (offset by the sticky bar),
 *   - scroll-spy: highlight the nav link whose section is in view,
 *   - keeps the active link scrolled into view inside the horizontal bar.
 *
 * Dependency-free, namespaced, and a no-op when the block isn't present.
 */
(function () {
	/** @type {HTMLElement | null} */
	const nav = document.querySelector('[data-odds-guide-nav]');
	if (!nav) {
		return;
	}

	/** @type {HTMLAnchorElement[]} */
	const links = Array.from(nav.querySelectorAll('[data-odds-guide-nav-link]'));
	if (!links.length) {
		return;
	}

	const ACTIVE_CLASS = 'is-active';

	/**
	 * Map each nav link to its target section element (when present on page).
	 * @type {{ link: HTMLAnchorElement, section: HTMLElement }[]}
	 */
	const pairs = [];
	for (const link of links) {
		const id = link.getAttribute('data-target') || '';
		if (!id) {
			continue;
		}
		const section = document.getElementById(id);
		if (section) {
			pairs.push({ link, section });
		}
	}

	/**
	 * The nav's current BOTTOM edge in the viewport — the true bottom of the
	 * sticky chrome. Because the nav is pinned just below the main site header,
	 * its bottom already accounts for that header (and the WP admin bar, when
	 * present). Used as the scroll offset so a jumped-to section title isn't
	 * hidden behind the stacked sticky chrome.
	 * @returns {number}
	 */
	const navBottom = () => Math.round(nav.getBoundingClientRect().bottom) || 0;

	/**
	 * Set the active link, de-activating the rest, and scroll it into view
	 * within the (possibly horizontally scrolling) bar.
	 * @param {HTMLAnchorElement | null} active
	 */
	let lastActive = null;
	const setActive = (active) => {
		if (active === lastActive) {
			return;
		}
		lastActive = active;
		for (const link of links) {
			const on = link === active;
			link.classList.toggle(ACTIVE_CLASS, on);
			if (on) {
				link.setAttribute('aria-current', 'true');
			} else {
				link.removeAttribute('aria-current');
			}
		}
		if (active && typeof active.scrollIntoView === 'function') {
			// Keep the active chip visible in the horizontal scroller without
			// hijacking vertical page scroll.
			const list = active.closest('.odds-guide-nav__inner');
			if (list && list.scrollWidth > list.clientWidth) {
				active.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
			}
		}
	};

	// --- Smooth-scroll on click ------------------------------------------------
	for (const link of links) {
		link.addEventListener('click', (e) => {
			const id = link.getAttribute('data-target') || '';
			const section = id ? document.getElementById(id) : null;
			if (!section) {
				return; // let the browser handle a missing anchor.
			}
			e.preventDefault();
			const top = window.pageYOffset + section.getBoundingClientRect().top - navBottom() - 8;
			window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
			setActive(link);
			// Reflect the anchor in the URL without an extra jump.
			if (window.history && typeof window.history.replaceState === 'function') {
				window.history.replaceState(null, '', '#' + id);
			}
		});
	}

	// --- Scroll-spy via IntersectionObserver -----------------------------------
	if (pairs.length && 'IntersectionObserver' in window) {
		/** @type {Map<HTMLElement, number>} ratio of each observed section */
		const ratios = new Map();

		const recompute = () => {
			let best = null;
			let bestRatio = 0;
			for (const { link, section } of pairs) {
				const r = ratios.get(section) || 0;
				if (r > bestRatio) {
					bestRatio = r;
					best = link;
				}
			}
			if (best) {
				setActive(best);
			}
		};

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					ratios.set(/** @type {HTMLElement} */(entry.target), entry.isIntersecting ? entry.intersectionRatio : 0);
				}
				recompute();
			},
			{
				// Trigger as a section crosses the area just below the sticky bar.
				rootMargin: `-${navBottom() + 8}px 0px -55% 0px`,
				threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
			}
		);

		for (const { section } of pairs) {
			observer.observe(section);
		}
	}
})();
